import { useState, useEffect } from 'react';
import { ShoppingCart } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';

interface StickyAddToCartProps {
  productName: string;
  price: number;
  variantId: string | undefined;
  targetRef: React.RefObject<HTMLElement | null>;
}

export default function StickyAddToCart({ productName, price, variantId, targetRef }: StickyAddToCartProps) {
  const [visible, setVisible] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    if (!targetRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(targetRef.current);
    return () => observer.disconnect();
  }, [targetRef]);

  async function handleAdd() {
    if (!variantId) return;
    setIsAdding(true);
    try {
      await addItem(variantId);
      addToast('Added to cart!', 'success');
    } catch {
      addToast('Failed to add to cart', 'error');
    } finally {
      setIsAdding(false);
    }
  }

  if (!visible || !variantId) return null;

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 bg-surface border-t border-sage-200 shadow-lg',
        'px-4 py-3 flex items-center gap-3',
        'md:hidden',
        'animate-in slide-in-from-bottom duration-200'
      )}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-sage-900 truncate">{productName}</p>
        <p className="text-sm font-bold text-sage-800">{formatCurrency(price)}</p>
      </div>
      <button
        onClick={handleAdd}
        disabled={isAdding}
        className={cn(
          'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium',
          'bg-cta text-cta-fg hover:bg-cta-deep transition-colors',
          'disabled:opacity-50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600'
        )}
      >
        <ShoppingCart className="h-4 w-4" />
        {isAdding ? 'Adding...' : 'Add to Cart'}
      </button>
    </div>
  );
}
